import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import { Save, CheckCircle } from 'lucide-react';

export function StorageSettings({ data, setData, isSaving, onSave, IntegrationStatus }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-bold text-slate-900 font-[Poppins]">File Storage</h3>
          <p className="text-sm text-slate-500 mt-1">Configure where uploaded files are stored</p>
        </div>
        <IntegrationStatus enabled={data.enabled} />
      </div>
      
      <div className="space-y-4">
        {/* Emergent Storage Option */}
        <div className={`p-4 rounded-xl border-2 transition-all ${data.use_emergent_storage ? 'border-green-500 bg-green-50' : 'border-stone-200 bg-stone-50'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${data.use_emergent_storage ? 'bg-green-500' : 'bg-stone-300'}`}>
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-slate-900">Emergent Cloud Storage</p>
                <p className="text-sm text-slate-500">Free, managed storage included with your plan</p>
              </div>
            </div>
            <Switch
              checked={data.use_emergent_storage}
              onCheckedChange={(checked) => setData(prev => ({ ...prev, use_emergent_storage: checked }))}
              data-testid="use-emergent-storage"
            />
          </div>
        </div>
        
        {!data.use_emergent_storage && (
          <>
            <div>
              <Label>Provider</Label>
              <select
                value={data.provider}
                onChange={(e) => setData(prev => ({ ...prev, provider: e.target.value }))}
                className="w-full h-12 mt-1.5 border border-stone-200 rounded-lg px-3"
                data-testid="storage-provider"
              >
                <option value="s3">Amazon S3</option>
                <option value="gcs">Google Cloud Storage</option>
                <option value="azure">Azure Blob Storage</option>
                <option value="local">Local Storage</option>
              </select>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Access Key</Label>
                <Input
                  placeholder="Your access key"
                  value={data.access_key}
                  onChange={(e) => setData(prev => ({ ...prev, access_key: e.target.value }))}
                  className="h-12 mt-1.5"
                  data-testid="storage-access-key"
                />
              </div>
              <div>
                <Label>Secret Key</Label>
                <Input
                  type="password"
                  placeholder="Your secret key"
                  value={data.secret_key}
                  onChange={(e) => setData(prev => ({ ...prev, secret_key: e.target.value }))}
                  className="h-12 mt-1.5"
                  data-testid="storage-secret-key"
                />
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Bucket Name</Label>
                <Input
                  placeholder="my-bucket"
                  value={data.bucket_name}
                  onChange={(e) => setData(prev => ({ ...prev, bucket_name: e.target.value }))}
                  className="h-12 mt-1.5"
                  data-testid="storage-bucket"
                />
              </div>
              <div>
                <Label>Region</Label>
                <Input
                  placeholder="ap-southeast-1"
                  value={data.region}
                  onChange={(e) => setData(prev => ({ ...prev, region: e.target.value }))}
                  className="h-12 mt-1.5"
                  data-testid="storage-region"
                />
              </div>
            </div>
            
            <div>
              <Label>Custom Endpoint URL (Optional)</Label>
              <Input
                placeholder="https://custom-s3-endpoint.com"
                value={data.endpoint_url}
                onChange={(e) => setData(prev => ({ ...prev, endpoint_url: e.target.value }))}
                className="h-12 mt-1.5"
                data-testid="storage-endpoint"
              />
              <p className="text-xs text-slate-500 mt-1">For S3-compatible storage like MinIO, DigitalOcean Spaces, etc.</p>
            </div>
          </>
        )}
      </div>
      
      <Button 
        onClick={onSave}
        disabled={isSaving}
        className="mt-6 bg-slate-900 hover:bg-slate-800"
        data-testid="save-storage-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        {isSaving ? 'Saving...' : 'Save Storage Settings'}
      </Button>
    </div>
  );
}
